import { useRouter } from "next/router";
import Link from "next/link";
import { useState } from "react";

import { Button, Card } from "@chef/components";
import { AppShell } from "@chef/feature-app-shell";
import { Close, Warning } from "@chef/icons/small";
import {
  showNotification,
  useDeleteBillingAgreementMutation,
  useUpdateBillingAgreementMutation,
} from "@chef/state-management";
import { useIntercom } from "@chef/hooks";
import { ACCOUNT_STATUSES, BRAND_PATHS } from "@chef/constants";
import { FlexWrapper } from "@chef/smart-components";

import { DeliveryCutoffBanner } from "../../../components/DeliveryCutoffBanner";
import { intl } from "./DeleteAccountPage.Intl";

interface HttpError extends Error {
  status?: number;
}

export const DeleteAccountPage = () => {
  useIntercom(false);
  const router = useRouter();

  const [isLoading, setIsLoading] = useState(false);
  const [updateBillingAgreement] = useUpdateBillingAgreementMutation();
  const [deleteBillingAgreement] = useDeleteBillingAgreementMutation();

  const handleSubmitAccountDeletionRequest = async () => {
    setIsLoading(true);

    try {
      await deleteBillingAgreement(null).unwrap();

      showNotification({
        type: "warn",
        message: intl.YOUR_REQUEST_HAS_BEEN_RECEIVED,
      });

      router.push(BRAND_PATHS.DELETE_ACCOUNT_SUCCESS_PAGE);
    } catch (err) {
      if ((err as HttpError).status === 409) {
        // Response 409 is conflict. Delete request will be handled manually.
        showNotification({
          type: "warn",
          message: intl.YOUR_REQUEST_HAS_BEEN_RECEIVED,
        });

        await updateBillingAgreement({
          status: ACCOUNT_STATUSES.PAUSED,
        }).unwrap();
        router.push(BRAND_PATHS.DELETE_ACCOUNT_REQUEST_SENT_PAGE);
      } else {
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppShell.Main
      className="flex-grow bg-gradient-to-b from-background to-grey-2"
      grid
      noPadding
    >
      <FlexWrapper
        title={intl.DELETE_ACCOUNT}
        close={{
          href: BRAND_PATHS.MY_ACCOUNT_HREF,
          type: "link",
          children: <Close />,
        }}
        back={{ type: "hidden" }}
        background="white"
        className="min-h-screen md:min-h-[unset] md:my-16"
      >
        <DeliveryCutoffBanner />
        <div className="px-4 md:px-10">
          <div className="flex flex-col">
            <h2 className="mt-8 text-xl md:text-2xl text-error">
              <strong>
                {intl.WHEN_YOU_DELETE_YOUR_ACCOUNT_THE_FOLLOWING_WILL_HAPPEN}:
              </strong>
            </h2>
          </div>

          <div className="flex flex-col gap-4 p-6 mt-8 text-left border border-grey-2 bg-background">
            {intl.DISCLAIMERS.map((disclaimer) => {
              return (
                <div key={disclaimer} className="flex items-center">
                  <Warning className="text-error" />
                  <span className="ml-4 text-sm">{disclaimer}</span>
                </div>
              );
            })}
          </div>

          <p className="mt-8 text-sm md:px-8 md:mb-10">
            {intl.PLEASE_NOTE_THAT_OUTSTANDING_PAYMENTS_MUST_BE_PAID}
          </p>
        </div>
        <footer className="fixed left-0 right-0 bottom-20 md:bottom-0 md:relative">
          <Card
            noPadding
            className="flex justify-between w-full gap-2 py-3 md:gap-6 md:justify-end shadow-fab"
          >
            <Link href={BRAND_PATHS.MY_ACCOUNT_HREF} passHref legacyBehavior>
              <Button className="ml-2 w-44" outlined>
                {intl.CANCEL}
              </Button>
            </Link>

            <Button
              primary
              onClick={handleSubmitAccountDeletionRequest}
              loading={isLoading}
              className="!bg-error mr-2 md:mr-4 w-44"
            >
              {intl.DELETE_ACCOUNT}
            </Button>
          </Card>
        </footer>
      </FlexWrapper>
    </AppShell.Main>
  );
};
